/**
 * 主题控制。
 *
 * 默认跟随系统（`prefers-color-scheme`），用户可以在设置里手动锁定浅色或深色。
 * 一次性把三件事对齐：`data-theme` 属性、`color-scheme`、以及 iOS 状态栏用的
 * `<meta name="theme-color">`。少同步一个，就会出现"页面是深色但状态栏还是白的"。
 *
 * 主题必须是**全站唯一**的：不允许某个页面或区块自己反色。
 */

export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'fridge.theme';

/** 与 tokens.css 里的 --bg 保持一致。状态栏颜色必须跟页面背景对上。 */
const THEME_COLOR: Record<ResolvedTheme, string> = {
	light: '#f2f2f7',
	dark: '#0b0b0d',
};

const listeners = new Set<(theme: ResolvedTheme) => void>();

function systemTheme(): ResolvedTheme {
	if (typeof window === 'undefined' || !window.matchMedia) return 'light';
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function getThemeMode(): ThemeMode {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
	} catch {
		/* 隐私模式下不可用，回落到跟随系统 */
	}
	return 'system';
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
	return mode === 'system' ? systemTheme() : mode;
}

/** 把解析后的主题写到 DOM 上。只改属性，不触发 React 重渲染。 */
export function applyTheme(mode: ThemeMode): ResolvedTheme {
	const theme = resolveTheme(mode);
	const root = document.documentElement;

	root.dataset.theme = theme;
	root.style.colorScheme = theme;

	const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
	if (meta) meta.content = THEME_COLOR[theme];

	for (const listener of listeners) listener(theme);
	return theme;
}

export function setThemeMode(mode: ThemeMode): ResolvedTheme {
	try {
		localStorage.setItem(STORAGE_KEY, mode);
	} catch {
		/* 存不了就算，本次会话仍然生效 */
	}
	return applyTheme(mode);
}

/**
 * 启动时调用一次。返回取消订阅的函数。
 * 只有 mode 是 system 时才跟随系统变化，用户手动锁定了就不理会。
 */
export function initTheme(): () => void {
	applyTheme(getThemeMode());

	if (typeof window === 'undefined' || !window.matchMedia) return () => {};
	const query = window.matchMedia('(prefers-color-scheme: dark)');

	const onChange = (): void => {
		if (getThemeMode() === 'system') applyTheme('system');
	};

	query.addEventListener('change', onChange);
	return () => query.removeEventListener('change', onChange);
}

export function onThemeChange(listener: (theme: ResolvedTheme) => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

export const THEME_LABELS: Record<ThemeMode, string> = {
	system: '跟随系统',
	light: '浅色',
	dark: '深色',
};
